import type {
    LaunchActionDescriptor,
    ProviderAuthConfig,
    ProviderAuthMethod,
    ProviderConfigField,
    ProviderSetupInstructions,
    ProviderType,
} from '@bmai/shared';

export type WizardStepKind =
    | 'prerequisites'
    | 'chooseAuth'
    | 'preAuthConfig'
    | 'auth'
    | 'config'
    | 'verify';

interface BaseStep {
    kind: WizardStepKind;
    label: string;
    description?: string;
}

export interface PrerequisitesStep extends BaseStep {
    kind: 'prerequisites';
    /** True only for before-auth prereqs in create mode — gates Next on user acknowledgement. */
    requireAcknowledgement: boolean;
}

export interface ChooseAuthStep extends BaseStep {
    kind: 'chooseAuth';
    options: ProviderAuthMethod[];
}

export interface PreAuthConfigStep extends BaseStep {
    kind: 'preAuthConfig';
    fields: ProviderConfigField[];
}

export interface AuthStep extends BaseStep {
    kind: 'auth';
    method: ProviderAuthMethod;
    fields: ProviderConfigField[];
    /** True when the install-flow descriptor takes over this step (e.g. GitHub App). */
    installFlow: boolean;
    /** Launch-to-external-tool buttons declared on the auth config (e.g. AWS CloudFormation). */
    launchActions: LaunchActionDescriptor[];
}

export interface ConfigStep extends BaseStep {
    kind: 'config';
    fields: ProviderConfigField[];
}

export interface VerifyStep extends BaseStep {
    kind: 'verify';
}

export type WizardStep =
    | PrerequisitesStep
    | ChooseAuthStep
    | PreAuthConfigStep
    | AuthStep
    | ConfigStep
    | VerifyStep;

export interface InferConnectionStepsInput {
    providerType: ProviderType;
    mode: 'create' | 'edit';
    /** The currently-selected auth method; required when `authTypes.length > 1` and the user is choosing. */
    selectedAuthMethod: ProviderAuthMethod | null;
}

/**
 * Pure function: given a ProviderType and the current selection state,
 * returns the ordered list of wizard steps to render. Steps that don't
 * apply to this provider (e.g. no setup instructions, single auth type)
 * are omitted automatically — no per-provider step boilerplate needed.
 */
export function inferConnectionSteps({ providerType, mode, selectedAuthMethod }: InferConnectionStepsInput): WizardStep[] {
    const steps: WizardStep[] = [];

    const setupPosition = providerType.setupInstructions?.position ?? 'before-auth';
    if (providerType.setupInstructions && setupPosition === 'before-auth') {
        steps.push({
            kind: 'prerequisites',
            label: 'Prerequisites',
            description: providerType.setupInstructions.title,
            requireAcknowledgement: shouldRequireAck(providerType.setupInstructions, 'before-auth', mode),
        });
    }

    const authOptions = providerType.authTypes.map((a) => a.method);
    if (mode === 'create' && authOptions.length > 1) {
        steps.push({
            kind: 'chooseAuth',
            label: 'Authentication method',
            options: authOptions,
        });
    }

    const authConfig: ProviderAuthConfig | undefined = selectedAuthMethod
        ? providerType.authTypes.find((a) => a.method === selectedAuthMethod)
        : providerType.authTypes[0];

    const connectionFields = providerType.connectionFields ?? [];
    const preAuthFields = connectionFields.filter((f) => f.preOAuth);
    const postAuthFields = connectionFields.filter((f) => !f.preOAuth);

    const effectiveMethod = authConfig?.method ?? selectedAuthMethod ?? providerType.authTypes[0]?.method ?? null;

    if (mode === 'create' && preAuthFields.length > 0) {
        steps.push({
            kind: 'preAuthConfig',
            label: 'Provider details',
            description: effectiveMethod === 'OAuth' ? 'Required before sign-in' : 'Required before continuing',
            fields: preAuthFields,
        });
    }

    if (effectiveMethod && authConfig) {
        steps.push({
            kind: 'auth',
            label: authStepLabel(effectiveMethod, mode),
            method: effectiveMethod,
            fields: authConfig.fields,
            installFlow: mode === 'create' && !!providerType.installFlow,
            launchActions: authConfig.launchActions ?? [],
        });
    }

    if (postAuthFields.length > 0) {
        steps.push({
            kind: 'config',
            label: 'Configuration',
            fields: postAuthFields,
        });
    }

    if (providerType.setupInstructions && setupPosition === 'after-auth') {
        steps.push({
            kind: 'prerequisites',
            label: 'Finish setup',
            description: providerType.setupInstructions.title,
            requireAcknowledgement: shouldRequireAck(providerType.setupInstructions, 'after-auth', mode),
        });
    }

    steps.push({
        kind: 'verify',
        label: mode === 'edit' ? 'Review & save' : 'Verify & finish',
    });

    return steps;
}

/**
 * Resolve whether the prerequisites step should gate Next on an acknowledgement
 * checkbox. The provider's `required` flag wins outright when set; otherwise
 * default behavior is "gate only when before-auth in create mode".
 */
function shouldRequireAck(
    si: ProviderSetupInstructions,
    position: 'before-auth' | 'after-auth',
    mode: 'create' | 'edit',
): boolean {
    if (si.required === true) return true;
    if (si.required === false) return false;
    return mode === 'create' && position === 'before-auth';
}

function authStepLabel(method: ProviderAuthMethod, mode: 'create' | 'edit'): string {
    if (mode === 'edit') return 'Authentication';
    switch (method) {
        case 'OAuth':
            return 'Sign in';
        case 'AssumedRole':
            return 'AWS IAM role';
        case 'ServiceAccount':
            return 'Service account';
        case 'ClientCredentials':
            return 'Client credentials';
        case 'Webhook':
            return 'Webhook';
        case 'ApiKey':
        default:
            return 'API credentials';
    }
}
